import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import type { Conversation } from "@/features/messaging/types/chat";
import { cn } from "@/utils/cn";
import { formatRelativeTime } from "@/utils/formatRelativeTime";

function initialsFromName(name: string): string {
  const [first, second] = name.trim().split(/\s+/);
  return `${first?.[0] ?? ""}${second?.[0] ?? ""}`.toUpperCase() || "?";
}

interface ConversationListItemProps {
  conversation: Conversation;
  isActive?: boolean;
  onClick: () => void;
}

/** docs/ui/component-inventory.md's `ConversationListItem`: avatar left,
 * name+preview middle, timestamp+unread badge right. */
export function ConversationListItem({ conversation, isActive, onClick }: ConversationListItemProps) {
  const isUnread = conversation.unreadCount > 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 border-b border-border px-4 py-3 text-left hover:bg-muted",
        isActive && "bg-muted",
      )}
    >
      <Avatar size="md">
        {conversation.otherParty.avatarUrl && (
          <AvatarImage src={conversation.otherParty.avatarUrl} alt="" />
        )}
        <AvatarFallback>{initialsFromName(conversation.otherParty.name)}</AvatarFallback>
      </Avatar>

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className={cn("truncate text-body", isUnread ? "font-semibold" : "font-medium")}>
            {conversation.otherParty.name}
          </p>
          {conversation.lastMessage && (
            <span className="shrink-0 text-caption text-muted-foreground">
              {formatRelativeTime(conversation.lastMessage.createdAt)}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          <p
            className={cn(
              "truncate text-body-sm",
              isUnread ? "font-medium text-foreground" : "text-muted-foreground",
            )}
          >
            {conversation.lastMessage?.text ?? conversation.product.title}
          </p>
          {isUnread && (
            <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-caption font-medium text-primary-foreground">
              {conversation.unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

export function ConversationListItemSkeleton() {
  return (
    <div className="flex items-center gap-3 border-b border-border px-4 py-3">
      <Skeleton className="h-10 w-10 rounded-full" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-3 w-2/3" />
      </div>
    </div>
  );
}
